import React from 'react';
import { styled } from '@mui/material/styles';
import Tooltip, { TooltipProps } from '@mui/material/Tooltip';

const CustomHtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} arrow />
))(() => ({
  [`& .MuiTooltip-tooltip`]: {
    textAlign: 'center !important',
    fontWeight: '450 !important',
    backgroundColor: 'white !important',
    color: 'var(--Primary-text, #00274A) !important',
    fontFamily: 'var(--auro-font-family-default) !important',
    fontSize: '16px !important',
    lineHeight: 'normal !important',
    letterSpacing: '-0.32px !important',
    fontStyle: 'normal !important',
    background: '#fff !important',
    border: 'unset !important',
    padding: '15px 25px 10px 25px !important',
    borderRadius: '4px !important',
    boxShadow: '0px 0px 5px 0px rgba(0, 0, 0, 0.15) !important',
    marginTop: '10px !important',
  },
  [`& .MuiTooltip-arrow`]: {
    color: '#fff', // Arrow color matches tooltip background
    width: '1.3em', // Ensure proper width for the arrow
    height: '0.7em', // Ensure proper height for the arrow
    '&::before': {
      content: '""',
      position: 'absolute',
      borderStyle: 'solid',
      borderWidth: '0.3em 0.3em 0 0.3em', // Create the triangle shape
      borderColor: '#fff transparent transparent transparent', // Match the tooltip's background color
      transformOrigin: 'center',
      boxShadow: '0px 0px 5px rgba(0, 0, 0, 0.15)', // Add shadow for better visibility
    },
  },
}));

interface EllipsisTooltipProps {
  children: React.ReactNode;
  className?: string;
  title?: string;
  placement?: TooltipProps['placement'];
}

const EllipsisTooltip: React.FC<EllipsisTooltipProps> = ({
  children,
  className,
  title,
  placement = 'bottom',
}) => {
  const [isOverflowed, setIsOverflowed] = React.useState(false);
  const textRef = React.useRef<HTMLSpanElement>(null);

  React.useEffect(() => {
    const checkOverflow = () => {
      const element = textRef.current;
      if (element) {
        setIsOverflowed(element.scrollWidth > element.clientWidth);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);

    return () => window.removeEventListener('resize', checkOverflow);
  }, [children]);

  return (
    <CustomHtmlTooltip title={isOverflowed ? (title ?? children) : ''} placement={placement}>
      <span
        ref={textRef}
        className={className}
        style={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {children}
      </span>
    </CustomHtmlTooltip>
  );
};

export default EllipsisTooltip;
